import React, { useState, useMemo } from "react";

import { faHandshake, faTimes } from "@fortawesome/pro-light-svg-icons";
import { faInfoCircle, faUser } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { Theme } from "@material-ui/core";
import {
    Typography,
    Divider,
    makeStyles,
    FormControl,
    InputLabel,
    Grid,
    Collapse,
    FormControlLabel,
    Switch,
    Checkbox,
    Zoom,
    Snackbar,
    IconButton,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import type { FormikProps } from "formik";
import { Field } from "formik";
import { TextField } from "formik-material-ui";
import moment from "moment-timezone";
import { useObservableState, useObservable, useObservableCallback, pluckFirst } from "observable-hooks";
import { useTranslation } from "react-i18next";
import { map, switchMap, distinctUntilChanged, startWith } from "rxjs/operators";

import type {
    IResponse,
    ISiteSlim,
    SiteScheduleException,
    IResponseWithLoading,
    SiteScheduleExceptionDelete,
    SiteScheduleExceptionDeleteResponse,
    SiteScheduleExceptionInsert,
    SiteScheduleExceptionInsertResponse,
    IPartnerUserSlim,
} from "App/Modules/Models";
import type { ISiteScheduleExceptionsRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import { of } from "rxjs";
import type { DialogCloseReason, ValidationResponse } from "App/ReactComponents/Shared";
import {
    ToggleButtonGroup,
    ToggleButton,
    Dialog,
    LoadingError,
    DateTimePicker,
    formatTimeZone,
    loadingCompletedResponse,
    OkCancelDialog,
} from "App/ReactComponents/Shared";

import { analysisDisabled, analysisEnabled } from "App/Img/Icons";
import { Form } from "App/ReactComponents/Shared/Components/Forms/Form";

import { MinutesMenuItem, HoursMenuItem } from "./ScheduleExceptionsMenuItems";
import { ScheduleExceptionsTable } from "./ScheduleExceptionsTable";
import { getScheduleExceptionsValidationSchema } from "./scheduleExceptionsValidationSchema";

const useStyles = makeStyles((theme: Theme) => ({
    selectInput: {
        "& label": {
            lineHeight: `${theme.spacing(2)}px`,
        },
        "& .MuiInput-root": {
            lineHeight: `${theme.spacing(3)}px`,
            paddingTop: theme.spacing(0.25),
        },
    },
    toggleButtonGroup: {
        marginTop: theme.spacing(2),
    },
    toggleButtonGroupError: {
        borderColor: theme.palette.error.main,
        border: "2px solid",
    },
    toggleButtonGroupValidationText: {
        color: theme.palette.error.main,
        fontSize: "1.2rem",
        marginTop: "3px",
    },
    applyButtonBar: {
        display: "inline-flex",
        justifyContent: "flex-end",
        marginTop: theme.spacing(4),
        float: "right",
    },
    divider: {
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(4),
    },
    dividerFullWidth: {
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(-3),
        marginRight: theme.spacing(-3),
        marginTop: theme.spacing(4),
    },
    dividerLoading: {
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    info: {
        color: theme.palette.info.main,
        "& svg": {
            marginRight: theme.spacing(0.5),
        },
    },
    sectionHeading: {
        marginTop: theme.spacing(4),
        width: 900,
    },
    onBehalfOf: {
        display: "inline-flex",
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
    },
    onBehalfOf__partnerIcon: {
        marginRight: theme.spacing(1),
    },
    onBehalfOf__icon: {
        alignItems: "center",
        display: "flex",
        fontSize: "22px",
    },
}));

type ScheduleExceptionsDialogProps = {
    onClose: (reason: DialogCloseReason) => void;
    isOpen: boolean;
    site: ISiteSlim;
    siteScheduleExceptionsRepository: ISiteScheduleExceptionsRepository;
    requestingUser: IPartnerUserSlim;
};

type ExceptionChangeType = "ADD" | "END" | "DELETE";

export const ScheduleExceptionsDialog = (props: ScheduleExceptionsDialogProps) => {
    const { t } = useTranslation();
    let currentFormikProps: FormikProps<SiteScheduleExceptionInsert>;

    const [showAdvancedMode, setShowAdvancedMode] = useState(false);
    const [showHistoricExceptions, setShowHistoricExceptions] = useState(false);

    const [isAddSuccessOpen, setIsAddSuccessOpen] = useState(false);
    const [isConflictingAdjustmentDialogOpen, setIsConflictingAdjustmentDialogOpen] = useState(false);

    const classes = useStyles();

    const validationSchema = useMemo(() => getScheduleExceptionsValidationSchema(t), [t]);

    const [setLocalChange, localChange$] = useObservableCallback<{
        changeType: ExceptionChangeType;
        exception: SiteScheduleException | SiteScheduleExceptionDeleteResponse | SiteScheduleExceptionInsertResponse;
    }>((change) => change);

    const resetFormState = () => {
        setShowAdvancedMode(false);
        setShowHistoricExceptions(false);
        currentFormikProps.resetForm();
    };
    const resetFormStateOnAddSuccessful = () => {
        setShowAdvancedMode(false);
        currentFormikProps.resetForm();
    };
    const handleAddExceptionFormSubmitted = (data: SiteScheduleExceptionInsert) => {
        let submitData: SiteScheduleExceptionInsert;
        if (showAdvancedMode) {
            submitData = {
                isOnType: data.isOnType,
                dtStart: data.dtStart,
                dtStop: data.dtStop,
                overrideExistingExceptions: data.overrideExistingExceptions,
            };
        } else {
            submitData = {
                isOnType: data.isOnType,
                durationSeconds: data.durationSeconds,
                overrideExistingExceptions: data.overrideExistingExceptions,
            };
        }

        if (data.requestingUser) {
            submitData.requestingUser = data.requestingUser;
        }

        return props.siteScheduleExceptionsRepository.addScheduleException(props.site.id, submitData);
    };
    const handleAddExceptionSuccessful = (data: IResponse<SiteScheduleExceptionInsertResponse>) => {
        setLocalChange({ changeType: "ADD", exception: data.data });
        resetFormStateOnAddSuccessful();
        setIsAddSuccessOpen(true);
    };

    const handleAddExceptionFailed = (
        data: IResponse<SiteScheduleExceptionInsertResponse>,
        validationErrors: ValidationResponse,
    ) => {
        if (
            validationErrors?.[""]?.some((error) =>
                error.includes("Validation.ScheduleException.OverlappingRangesError"),
            )
        ) {
            setIsConflictingAdjustmentDialogOpen(true);
            return true;
        }
        return false;
    };

    const handleConflictingAdjustmentDialogClosed = async (reason: DialogCloseReason) => {
        if (reason === "okButtonClick") {
            currentFormikProps.setFieldValue("overrideExistingExceptions", true);
            await currentFormikProps.submitForm();
        }
        setIsConflictingAdjustmentDialogOpen(false);
    };

    const handleEndRemoveExceptionClick = (data: SiteScheduleException) => {
        const submitData: SiteScheduleExceptionDelete = {
            scheduleExceptionId: data.id,
            requestingUser: props.requestingUser ? props.requestingUser : null,
        };

        return props.siteScheduleExceptionsRepository
            .removeScheduleException(props.site.id, submitData)
            .subscribe((response) => {
                if (response.isSuccessful) {
                    if (response.data?.updated.length > 0) {
                        setLocalChange({
                            changeType: "END",
                            exception: response.data,
                        });
                    }

                    if (response.data?.deleted.length > 0) {
                        setLocalChange({
                            changeType: "DELETE",
                            exception: response.data,
                        });
                    }
                }
            });
    };

    const handleClose = (reason: DialogCloseReason) => {
        props.onClose(reason);
        resetFormState();
    };

    const siteId$ = useObservable(pluckFirst, [props.site?.id]);

    const exceptions$ = useObservable(() => {
        return siteId$.pipe(
            distinctUntilChanged(),
            switchMap((_siteId) => {
                if (!_siteId) {
                    return of(loadingCompletedResponse<SiteScheduleException[]>());
                }
                return extendWithLoadingAndFail(
                    props.siteScheduleExceptionsRepository.getScheduleExceptions(_siteId).pipe(
                        switchMap((response) =>
                            localChange$.pipe(
                                startWith(null),
                                map((localChange) => {
                                    if (response.data && localChange) {
                                        switch (localChange.changeType) {
                                            case "ADD":
                                                // eslint-disable-next-line no-case-declarations
                                                const addResponse =
                                                    localChange.exception as SiteScheduleExceptionInsertResponse;

                                                addResponse?.added?.forEach((a) => {
                                                    response.data.push(a);
                                                });

                                                addResponse?.updated?.forEach((e) => {
                                                    const existingException = response.data?.find(
                                                        (existingE) => existingE.id === e.id,
                                                    );
                                                    if (existingException) {
                                                        Object.assign(existingException, e);
                                                    } else {
                                                        response.data.push(e);
                                                    }
                                                });
                                                addResponse?.deleted?.forEach((e) => {
                                                    response.data.splice(
                                                        response.data.findIndex((existinE) => existinE.id === e.id),
                                                        1,
                                                    );
                                                });
                                                break;
                                            case "DELETE":
                                                response.data.splice(
                                                    response.data.findIndex(
                                                        (ex) =>
                                                            ex.id ===
                                                            (localChange.exception as SiteScheduleException).id,
                                                    ),
                                                    1,
                                                );
                                                break;
                                            case "END":
                                                // eslint-disable-next-line no-case-declarations
                                                const endResponse =
                                                    localChange.exception as SiteScheduleExceptionDeleteResponse;

                                                endResponse?.updated?.forEach((e) => {
                                                    const existingException = response.data?.find(
                                                        (existingE) => existingE.id === e.id,
                                                    );
                                                    if (existingException) {
                                                        Object.assign(existingException, e);
                                                    } else {
                                                        response.data.push(e);
                                                    }
                                                });
                                                endResponse?.deleted?.forEach((e) => {
                                                    response.data.splice(
                                                        response.data.findIndex((existinE) => existinE.id === e.id),
                                                        1,
                                                    );
                                                });
                                                break;
                                            default:
                                                throw new Error("Unknown change type");
                                        }
                                    }
                                    if (response.data) {
                                        response.data.sort((a, b) => moment(a.dtStart).diff(b.dtStart));
                                    }
                                    return response;
                                }),
                            ),
                        ),
                    ),
                );
            }),
        );
    });

    const allExceptions$ = useObservable(() =>
        exceptions$.pipe(
            map((response) => {
                const { data, ...state } = response;
                if (data) {
                    const now = moment();
                    return {
                        currentExceptions: {
                            ...state,
                            data: data.filter((ex) => now.isBetween(ex.dtStart, ex.dtStop)),
                        } as IResponseWithLoading<SiteScheduleException[]>,
                        upcomingExceptions: {
                            ...state,
                            data: data.filter((ex) => ex.dtStart.isAfter(now)),
                        } as IResponseWithLoading<SiteScheduleException[]>,
                        historicExceptions: {
                            ...state,
                            data: data.filter((ex) => ex.dtStop.isBefore(now)),
                        } as IResponseWithLoading<SiteScheduleException[]>,
                    };
                }
                return {
                    currentExceptions: response,
                    upcomingExceptions: response,
                    historicExceptions: response,
                };
            }),
        ),
    );

    const { currentExceptions, upcomingExceptions, historicExceptions } = useObservableState(allExceptions$, {
        currentExceptions: undefined,
        upcomingExceptions: undefined,
        historicExceptions: undefined,
    });
    const { data: currentExceptionsData, ...currentExceptionsLoadingProps } = currentExceptions;

    const handleToggleAdvancedMode = (newState: boolean) => {
        // eslint-disable-next-line @typescript-eslint/unbound-method -- Formik needs funcs to be unbound "function () {}" and not "() => {}" since "this" is injected by Formik
        const { setFieldValue, touched, values } = currentFormikProps;
        if (newState) {
            const fuzzyNow = moment().add(5, "minutes");

            if (
                (!touched.dtStart || !values.dtStart || fuzzyNow.isAfter(values.dtStart)) &&
                (!touched.dtStop || !values.dtStop || fuzzyNow.isAfter(values.dtStop))
            ) {
                const now = moment().tz(props.site.timeZone).add(1, "minute").startOf("minute");
                setFieldValue("dtStart", now);
                setFieldValue("dtStop", now.clone().add(values.durationSeconds ?? 0, "seconds"));
            }
        }
        setFieldValue("showAdvancedMode", newState);
        setShowAdvancedMode(newState);
    };

    return props.isOpen ? (
        <Dialog
            dialogOptions={{ maxWidth: "lg" }}
            onClose={(reason) => handleClose(reason)}
            open={props.isOpen}
            title={t("Entities.ScheduleException.CreateNew")}
            subTitle={props.site?.name}
            subTitle2={formatTimeZone(props.site.timeZone)}
        >
            <Form<SiteScheduleExceptionInsert, SiteScheduleExceptionInsertResponse>
                initialValues={{
                    isOnType: null,
                    durationSeconds: "" as any,
                    dtStart: null,
                    dtStop: null,
                    overrideExistingExceptions: undefined,
                    requestingUser: props.requestingUser,
                }}
                extraButtons={({ isSubmitting }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showAdvancedMode}
                                disabled={isSubmitting}
                                onChange={(_, checked) => handleToggleAdvancedMode(checked)}
                                color="primary"
                            />
                        }
                        label={t("Entities.ScheduleException.Advanced")}
                    />
                )}
                onSubmitSuccessful={(data) => handleAddExceptionSuccessful(data)}
                onSubmitFailed={(data, validationErrors) => handleAddExceptionFailed(data, validationErrors)}
                onSubmitting={(data) => handleAddExceptionFormSubmitted(data)}
                validationSchema={validationSchema as any}
                buttonContainer={({ children }) => <div className={classes.applyButtonBar}>{children}</div>}
            >
                {(formikProps) => {
                    currentFormikProps = formikProps;

                    // eslint-disable-next-line @typescript-eslint/unbound-method -- Formik needs funcs to be unbound "function () {}" and not "() => {}" since "this" is injected by Formik
                    const { errors, setFieldValue, values } = formikProps;

                    return (
                        <>
                            <Grid container spacing={4}>
                                <Grid item sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink>
                                            {t("Entities.ScheduleException.AnalysisShouldBe")}
                                        </InputLabel>
                                        <ToggleButtonGroup
                                            className={
                                                errors.isOnType
                                                    ? `${classes.toggleButtonGroup} ${classes.toggleButtonGroupError}`
                                                    : classes.toggleButtonGroup
                                            }
                                            fullWidth
                                        >
                                            <ToggleButton
                                                active={values.isOnType === true}
                                                onClick={() => setFieldValue("isOnType", true)}
                                                startIcon={<FontAwesomeIcon fixedWidth icon={analysisEnabled} />}
                                            >
                                                {t("Entities.ScheduleException.Enabled")}
                                            </ToggleButton>
                                            <ToggleButton
                                                active={values.isOnType === false}
                                                onClick={() => setFieldValue("isOnType", false)}
                                                startIcon={<FontAwesomeIcon fixedWidth icon={analysisDisabled} />}
                                            >
                                                {t("Entities.ScheduleException.Disabled")}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                        {errors.isOnType ? (
                                            <Typography className={classes.toggleButtonGroupValidationText}>
                                                Required
                                            </Typography>
                                        ) : null}
                                    </FormControl>
                                </Grid>
                                <Grid item sm={6}>
                                    <Zoom in={!showAdvancedMode} style={{ transformOrigin: "0 0 0" }}>
                                        <FormControl
                                            className={classes.selectInput}
                                            fullWidth
                                            error={!!errors.durationSeconds}
                                        >
                                            <Field
                                                component={TextField}
                                                select
                                                name="durationSeconds"
                                                label={t("Entities.ScheduleException.StartingNow")}
                                                fullWidth
                                            >
                                                {MinutesMenuItem(10, t)}
                                                {MinutesMenuItem(15, t)}
                                                {MinutesMenuItem(20, t)}
                                                {MinutesMenuItem(30, t)}
                                                {MinutesMenuItem(45, t)}
                                                {HoursMenuItem(1, t)}
                                                {HoursMenuItem(2, t)}
                                                {HoursMenuItem(3, t)}
                                                {HoursMenuItem(4, t)}
                                                {HoursMenuItem(6, t)}
                                                {HoursMenuItem(8, t)}
                                                {HoursMenuItem(12, t)}
                                                {HoursMenuItem(16, t)}
                                                {HoursMenuItem(24, t)}
                                            </Field>
                                        </FormControl>
                                    </Zoom>
                                </Grid>
                            </Grid>
                            <Collapse in={showAdvancedMode}>
                                <Grid container spacing={4}>
                                    <Grid item sm={6}>
                                        <FormControl fullWidth error={!!errors.dtStart}>
                                            <Field
                                                component={DateTimePicker}
                                                name="dtStart"
                                                label={t("Entities.Schedule.StartTime")}
                                                timeZone={props.site.timeZone}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item sm={6}>
                                        <FormControl fullWidth error={!!errors.dtStart}>
                                            <Field
                                                component={DateTimePicker}
                                                name="dtStop"
                                                label={t("Entities.Schedule.StopTime")}
                                                timeZone={props.site.timeZone}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Collapse>
                            <Snackbar
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                                open={isAddSuccessOpen}
                                autoHideDuration={6000}
                                onClose={() => setIsAddSuccessOpen(false)}
                                message="Success"
                            >
                                <Alert
                                    onClose={() => setIsAddSuccessOpen(false)}
                                    severity="success"
                                    action={
                                        <IconButton
                                            size="small"
                                            aria-label="close"
                                            color="inherit"
                                            onClick={() => setIsAddSuccessOpen(false)}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </IconButton>
                                    }
                                >
                                    {t("Entities.ScheduleException.AddedSuccessfully")}
                                </Alert>
                            </Snackbar>
                            {props.requestingUser !== null ? (
                                <div className={`card card--nested card--shaded card--flex ${classes.onBehalfOf}`}>
                                    <div className={classes.onBehalfOf__icon}>
                                        <FontAwesomeIcon icon={faUser} fixedWidth />
                                    </div>
                                    <div className="data-panels data-panels--rows data-panels--no-spacing">
                                        <div className="data-panels__panel">
                                            <div className="data-panels__heading">
                                                <p className="caption">
                                                    {t("Entities.ScheduleException.CreatingOnBehalfOf")}
                                                </p>
                                            </div>
                                            <div className="data-panels__heading">
                                                <p className="paragraph paragraph--bold">
                                                    {props.requestingUser.fullName}
                                                </p>
                                            </div>
                                            <div className="data-panels__heading">
                                                <p className="caption caption--bold">
                                                    <FontAwesomeIcon
                                                        icon={faHandshake}
                                                        fixedWidth
                                                        className={classes.onBehalfOf__partnerIcon}
                                                    />
                                                    {props.requestingUser.partner.name}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    );
                }}
            </Form>

            <LoadingError {...currentExceptionsLoadingProps} keepMounted className={classes.dividerLoading} />

            <Typography variant="h3" className={classes.sectionHeading} gutterBottom>
                {t("Entities.ScheduleException.CurrentScheduleExceptions")}
            </Typography>

            <ScheduleExceptionsTable
                analysisEnabledCaption={t("Entities.ScheduleException.AnalysisIs")}
                onRemove={(exception) => handleEndRemoveExceptionClick(exception)}
                removeButtonCaption={t("Entities.ScheduleException.EndNow")}
                scheduleExceptions={currentExceptions?.data}
                site={props.site}
                sortOrder="asc"
            />

            <Typography variant="h3" className={classes.sectionHeading} gutterBottom>
                {t("Entities.ScheduleException.UpcomingScheduleExceptions")}
            </Typography>

            <ScheduleExceptionsTable
                analysisEnabledCaption={t("Entities.ScheduleException.AnalysisWillBe")}
                onRemove={(exception) => handleEndRemoveExceptionClick(exception)}
                removeButtonCaption={t("Gui.Buttons.Remove")}
                scheduleExceptions={upcomingExceptions?.data}
                site={props.site}
                sortOrder="asc"
            />

            <Typography variant="h3" className={classes.sectionHeading} gutterBottom>
                {t("Entities.ScheduleException.HistoricalScheduleExceptions")}
            </Typography>
            {historicExceptions?.data?.length === 0 ? (
                <>{t("Entities.Schedule.NoScheduleExceptions")}</>
            ) : (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showHistoricExceptions}
                                onChange={() => setShowHistoricExceptions(!showHistoricExceptions)}
                            />
                        }
                        label={t("Entities.ScheduleException.ShowHistoricalScheduleExceptions")}
                    />
                    <Collapse in={showHistoricExceptions}>
                        <ScheduleExceptionsTable
                            analysisEnabledCaption={t("Entities.ScheduleException.AnalysisWas")}
                            scheduleExceptions={historicExceptions?.data}
                            site={props.site}
                            sortOrder="desc"
                        />
                    </Collapse>
                </>
            )}
            <OkCancelDialog
                open={isConflictingAdjustmentDialogOpen}
                title={t("Entities.ScheduleException.OverlapOverrideConfirmationTitle")}
                onClose={(reason) => handleConflictingAdjustmentDialogClosed(reason)}
                okButtonText="Gui.Buttons.Override"
            >
                {t("Entities.ScheduleException.OverlapOverrideConfirmationBody")}
            </OkCancelDialog>
            <Divider className={classes.dividerFullWidth} />
            <Typography variant="body2" className={classes.info}>
                <FontAwesomeIcon icon={faInfoCircle} fixedWidth />
                {t("Entities.ScheduleException.OverlapInfo")}
            </Typography>
        </Dialog>
    ) : null;
};
